import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import type {
  RuteroConnectorOccupancyLimits,
  RuteroOccupancy,
  RuteroOfferQuoteCategory,
  RuteroOfferQuoteSupplement,
  RuteroSelectedSupplement,
} from '@bandido/shared/types';

import type { OfferOptionsSelectionPreview } from '../queries';
import { releaseNumberInputOnWheel } from '../numberInput';
import {
  circuitSupplementEligible,
  reconcileCircuitSupplements,
  setCircuitRoomCategory,
  setCircuitSupplement,
} from '../circuitOccupancy';

interface Props {
  occupancy: RuteroOccupancy;
  limits: RuteroConnectorOccupancyLimits;
  /** Facts of the fresh quote only; never used as the editable catalogue. */
  preview: OfferOptionsSelectionPreview | undefined;
  categories: readonly RuteroOfferQuoteCategory[];
  publishedSupplements: readonly RuteroOfferQuoteSupplement[];
  supplements: RuteroSelectedSupplement[];
  onOccupancyChange: (occupancy: RuteroOccupancy) => void;
  onSupplementsChange: (supplements: RuteroSelectedSupplement[]) => void;
}

export function CircuitQuoteOptions({
  occupancy,
  limits,
  preview,
  categories,
  publishedSupplements,
  supplements,
  onOccupancyChange,
  onSupplementsChange,
}: Props) {
  const { t } = useTranslation();
  const passengerScopes = occupancy.rooms.flatMap((room, roomIndex) =>
    room.ages.map((age, indexInRoom) => ({
      roomIndex,
      age,
      indexInRoom,
    })),);
  const changeOccupancy = (next: RuteroOccupancy) => {
    onOccupancyChange(next);
    onSupplementsChange(
      reconcileCircuitSupplements(
        supplements,
        publishedSupplements,
        next,
      ),
    );
  };

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Box>
        <Typography variant="subtitle2">
          {t('travelServices.circuits.quote.rooms')}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          mb={1}
        >
          {t('travelServices.circuits.quote.roomsHint')}
        </Typography>
        <Box display="flex" flexDirection="column" gap={1}>
          {occupancy.rooms.map((room, roomIndex) => (
            <Box
              key={roomIndex}
              border={1}
              borderColor="divider"
              borderRadius={1}
              p={1.25}
            >
              <Typography variant="body2" fontWeight={700}>
                {t('travelServices.circuits.occupancy.room', {
                  number: roomIndex + 1,
                })}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                {t('travelServices.circuits.quote.ages', {
                  ages: room.ages.join(', '),
                })}
              </Typography>
              {categories.length > 0 && (
                <Box display="flex" gap={0.75} flexWrap="wrap" mt={1}>
                  {categories.map((category) => (
                    <Chip
                      key={category.id}
                      clickable
                      color={
                        room.categoryId === category.id ? 'primary' : 'default'
                      }
                      variant={
                        room.categoryId === category.id ? 'filled' : 'outlined'
                      }
                      label={category.name}
                      disabled={
                        limits.sameCategoryForAllRooms
                          ? occupancy.rooms.some(
                            (candidate) =>
                              category.occupancyRange !== undefined &&
                              (candidate.ages.length < category.occupancyRange.min ||
                                candidate.ages.length > category.occupancyRange.max),
                          )
                          : category.occupancyRange !== undefined &&
                            (room.ages.length < category.occupancyRange.min ||
                              room.ages.length > category.occupancyRange.max)
                      }
                      onClick={() =>
                        changeOccupancy(
                          setCircuitRoomCategory(
                            occupancy,
                            roomIndex,
                            room.categoryId === category.id
                              ? undefined
                              : category.id,
                            limits.sameCategoryForAllRooms,
                          ),
                        )
                      }
                    />
                  ))}
                </Box>
              )}
            </Box>
          ))}
        </Box>
      </Box>

      {publishedSupplements.length > 0 && (
        <Box>
          <Typography variant="subtitle2">
            {t('travelServices.circuits.quote.supplements')}
          </Typography>
          <Box display="flex" flexDirection="column" gap={1} mt={1}>
            {publishedSupplements.flatMap((supplement) => {
              const scopes: {
                label: string;
                roomIndex?: number;
                passengerIndex?: number;
                eligibilityRoomIndex?: number;
              }[] =
                supplement.per === 'passenger'
                  ? passengerScopes.map((scope, passengerIndex) => ({
                    passengerIndex,
                    eligibilityRoomIndex: scope.roomIndex,
                    label: t('travelServices.circuits.quote.passengerScope', {
                      passenger: passengerIndex + 1,
                      room: scope.roomIndex + 1,
                    }),
                  }))
                  : supplement.per === 'room'
                    ? occupancy.rooms.map((_, roomIndex) => ({
                      roomIndex,
                      eligibilityRoomIndex: roomIndex,
                      label: t('travelServices.circuits.occupancy.room', {
                        number: roomIndex + 1,
                      }),
                    }))
                    : [
                      {
                        label: t(
                          'travelServices.circuits.quote.bookingScope',
                        ),
                      },
                    ];

              return scopes.flatMap((scope) => {
                if (
                  (scope.roomIndex !== undefined ||
                    scope.passengerIndex !== undefined) &&
                  !circuitSupplementEligible(
                    supplement,
                    occupancy,
                    scope.eligibilityRoomIndex ?? 0,
                    scope.passengerIndex,
                  )
                ) {
                  return [];
                }
                const identity = {
                  code: supplement.code,
                  ...(scope.roomIndex === undefined
                    ? {}
                    : { roomIndex: scope.roomIndex }),
                  ...(scope.passengerIndex === undefined
                    ? {}
                    : { passengerIndex: scope.passengerIndex }),
                };
                const selected = supplements.find(
                  (entry) =>
                    entry.code === identity.code &&
                    entry.roomIndex === identity.roomIndex &&
                    entry.passengerIndex === identity.passengerIndex,
                );
                const replace = (next: RuteroSelectedSupplement | undefined) =>
                  onSupplementsChange(
                    setCircuitSupplement(supplements, next, identity),
                  );
                const details = selected?.transfer;
                return [
                  <Box
                    key={`${supplement.code}-${scope.roomIndex ?? ''}-${scope.passengerIndex ?? ''}`}
                    border={1}
                    borderColor="divider"
                    borderRadius={1}
                    p={1.25}
                  >
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      gap={1}
                      alignItems="flex-start"
                    >
                      <Box minWidth={0}>
                        <Typography variant="body2" fontWeight={600}>
                          {supplement.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {scope.label}
                        </Typography>
                      </Box>
                      <FormControlLabel
                        label={t('travelServices.circuits.quote.addSupplement')}
                        control={
                          <Switch
                            size="small"
                            checked={Boolean(selected)}
                            onChange={(event) =>
                              replace(
                                event.target.checked
                                  ? { ...identity, quantity: 1 }
                                  : undefined,
                              )
                            }
                          />
                        }
                      />
                    </Box>
                    {selected && supplement.per === 'night' && (
                      <TextField
                        size="small"
                        type="number"
                        label={t('travelServices.circuits.quote.nights')}
                        value={selected.quantity ?? 1}
                        inputProps={{
                          min: 1,
                          max: supplement.limits?.maxNights,
                          onWheel: releaseNumberInputOnWheel,
                        }}
                        onChange={(event) => {
                          const quantity = Number(event.target.value);
                          if (!Number.isInteger(quantity) || quantity < 1) return;
                          if (
                            supplement.limits?.maxNights !== undefined &&
                            quantity > supplement.limits.maxNights
                          ) return;
                          replace({ ...selected, quantity });
                        }}
                        sx={{ mt: 1, width: 140 }}
                      />
                    )}
                    {selected && supplement.requiresDetail === 'transfer' && (
                      <Box
                        display="grid"
                        gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }}
                        gap={1}
                        mt={1}
                      >
                        {(
                          [
                            'airport',
                            'airline',
                            'flightNumber',
                            'dateTime',
                            'city',
                          ] as const
                        ).map((field) => (
                          <TextField
                            key={field}
                            size="small"
                            type={
                              field === 'dateTime' ? 'datetime-local' : 'text'
                            }
                            required={
                              field === 'airport' ||
                              field === 'flightNumber' ||
                              field === 'dateTime'
                            }
                            label={t(
                              `travelServices.circuits.quote.transfer.${field}`,
                            )}
                            value={details?.[field] ?? ''}
                            InputLabelProps={
                              field === 'dateTime'
                                ? { shrink: true }
                                : undefined
                            }
                            onChange={(event) =>
                              replace({
                                ...selected,
                                transfer: {
                                  airport: details?.airport ?? '',
                                  flightNumber: details?.flightNumber ?? '',
                                  dateTime: details?.dateTime ?? '',
                                  ...(details?.airline
                                    ? { airline: details.airline }
                                    : {}),
                                  ...(details?.city
                                    ? { city: details.city }
                                    : {}),
                                  [field]: event.target.value,
                                },
                              })
                            }
                          />
                        ))}
                      </Box>
                    )}
                  </Box>,
                ];
              });
            })}
          </Box>
        </Box>
      )}

      {preview?.breakdown && preview.breakdown.length > 0 && (
        <Box>
          <Typography variant="subtitle2">
            {t('travelServices.circuits.quote.breakdown')}
          </Typography>
          {preview.breakdown.map((line, index) => (
            <Box
              key={`${line.concept}-${index}`}
              display="flex"
              gap={1}
            >
              <Typography variant="body2">{line.concept}</Typography>
              {line.quantity !== undefined && (
                <Typography variant="caption" color="text.secondary">
                  × {line.quantity}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      )}

      {preview?.conditionsUrl && (
        <Button
          component="a"
          href={preview.conditionsUrl}
          target="_blank"
          rel="noreferrer"
          sx={{ alignSelf: 'flex-start' }}
        >
          {t('travelServices.circuits.quote.conditions')}
        </Button>
      )}

      {preview?.message && <Alert severity="info">{preview.message}</Alert>}
    </Box>
  );
}
